import React, { useState, useEffect, useRef, useContext } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import { LuCalendar, LuChevronLeft, LuChevronRight } from "react-icons/lu";
import { Language } from "../../App";
import "./ModernDateTimePicker.css";

const MONTHS = [
  { index: 0, key: "jan", label: "Jan", fullName: "January" },
  { index: 1, key: "feb", label: "Feb", fullName: "February" },
  { index: 2, key: "mar", label: "Mar", fullName: "March" },
  { index: 3, key: "apr", label: "Apr", fullName: "April" },
  { index: 4, key: "may", label: "May", fullName: "May" },
  { index: 5, key: "jun", label: "Jun", fullName: "June" },
  { index: 6, key: "jul", label: "Jul", fullName: "July" },
  { index: 7, key: "aug", label: "Aug", fullName: "August" },
  { index: 8, key: "sep", label: "Sep", fullName: "September" },
  { index: 9, key: "oct", label: "Oct", fullName: "October" },
  { index: 10, key: "nov", label: "Nov", fullName: "November" },
  { index: 11, key: "dec", label: "Dec", fullName: "December" },
];

export default function ModernMonthPicker({
  value,
  onChange,
  minDate,
  maxDate,
  placeholder = "Select Month",
  disabled = false,
  onDisabledClick,
  error = false,
  className = "",
  label,
  id,
  renderTrigger,
}) {
  const { lang } = useContext(Language) || {};
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Normalize initial value to Moment
  const parseValue = (val) => {
    if (!val) return null;
    if (moment.isMoment(val) && val.isValid()) return val;
    if (typeof val === "object" && val.month && val.year) {
      return moment(`${val.year}-${val.month}-01`, "YYYY-MM-DD");
    }
    if (typeof val === "string" && moment(val).isValid()) {
      return moment(val);
    }
    return null;
  };

  const selectedMoment = parseValue(value);

  // Year state
  const [viewYear, setViewYear] = useState(() => {
    return selectedMoment ? selectedMoment.year() : moment().year();
  });

  // Temporary selected moment before clicking Apply
  const [pendingMonth, setPendingMonth] = useState(selectedMoment);

  useEffect(() => {
    const parsed = parseValue(value);
    if (parsed) {
      setPendingMonth(parsed);
      setViewYear(parsed.year());
    } else {
      setPendingMonth(null);
      setViewYear(moment().year());
    }
  }, [value, isOpen]);

  // Handle ESC key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const handleTriggerClick = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (disabled) {
      if (onDisabledClick) onDisabledClick();
      return;
    }
    setIsOpen((prev) => !prev);
  };

  const handlePrevYear = (e) => {
    e.stopPropagation();
    setViewYear((prev) => prev - 1);
  };

  const handleNextYear = (e) => {
    e.stopPropagation();
    setViewYear((prev) => prev + 1);
  };

  const handleSelectMonth = (monthIndex) => {
    const newMoment = moment().year(viewYear).month(monthIndex).startOf("month");
    setPendingMonth(newMoment);
  };

  const handleApply = (e) => {
    e.stopPropagation();
    if (pendingMonth && onChange) {
      const monthNum = String(pendingMonth.month() + 1).padStart(2, "0");
      const yearStr = String(pendingMonth.year());
      onChange({
        month: monthNum,
        year: yearStr,
        moment: pendingMonth,
        formatted: pendingMonth.format("MMMM YYYY"),
      });
    }
    setIsOpen(false);
  };

  const handleCancel = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setPendingMonth(selectedMoment);
    setIsOpen(false);
  };

  const isMonthDisabled = (monthIndex) => {
    const monthMoment = moment().year(viewYear).month(monthIndex).startOf("month");
    if (minDate && moment(minDate).isValid() && monthMoment.endOf("month").isBefore(moment(minDate).startOf("day"))) {
      return true;
    }
    if (maxDate && moment(maxDate).isValid() && monthMoment.startOf("month").isAfter(moment(maxDate).endOf("day"))) {
      return true;
    }
    return false;
  };

  const isPrevYearDisabled = minDate && moment(minDate).isValid() && moment().year(viewYear - 1).endOf("year").isBefore(moment(minDate).startOf("day"));
  const isNextYearDisabled = maxDate && moment(maxDate).isValid() && moment().year(viewYear + 1).startOf("year").isAfter(moment(maxDate).endOf("day"));

  const displayValue = selectedMoment ? selectedMoment.format("MMMM YYYY") : "";

  return (
    <div className={`position-relative ${renderTrigger ? "d-inline-block" : "w-100"} ${className}`} ref={containerRef} id={id}>
      {label && <label className="agenda-drawer-label mb-1 d-block">{label}</label>}

      {renderTrigger ? (
        renderTrigger({
          isOpen,
          displayValue,
          placeholder,
          onClick: handleTriggerClick,
          onClear: (e) => {
            if (e && e.stopPropagation) e.stopPropagation();
            if (onChange) onChange(null);
          },
          selectedMoment,
        })
      ) : (
        <div
          className={`mdtp-trigger-box ${error ? "has-error" : ""} ${disabled ? "is-disabled" : ""} ${isOpen ? "is-focused" : ""}`}
          onClick={handleTriggerClick}
          role="button"
          tabIndex={disabled ? -1 : 0}
        >
          <span className={`mdtp-trigger-value ${!displayValue ? "is-placeholder" : ""}`}>
            {displayValue || placeholder}
          </span>
          <span className="mdtp-trigger-icon">
            <LuCalendar />
          </span>
        </div>
      )}

      {isOpen &&
        createPortal(
          <div className="mdtp-modal-overlay" onClick={handleCancel}>
            <div
              className="mdtp-modal-dialog"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "340px" }}
            >
              {/* Header: Year Navigator */}
              <div className="mdtp-header">
                <span className="mdtp-header-title">{viewYear}</span>
                <div className="mdtp-header-nav">
                  <button
                    type="button"
                    className="mdtp-nav-btn"
                    onClick={handlePrevYear}
                    disabled={isPrevYearDisabled}
                    title="Previous year"
                  >
                    <LuChevronLeft size={16} />
                  </button>
                  <button
                    type="button"
                    className="mdtp-nav-btn"
                    onClick={handleNextYear}
                    disabled={isNextYearDisabled}
                    title="Next year"
                  >
                    <LuChevronRight size={16} />
                  </button>
                </div>
              </div>

              {/* Selected Summary Strip */}
              <div className="mdtp-summary-strip">
                <div className="mdtp-summary-content">
                  <LuCalendar className="mdtp-summary-icon" size={14} />
                  <span className="mdtp-summary-text">
                    {pendingMonth ? pendingMonth.format("MMMM YYYY") : lang?.select_month || "Select a month"}
                  </span>
                </div>
              </div>

              {/* 12 Months Grid (3 columns x 4 rows) */}
              <div className="mdtp-month-grid">
                {MONTHS.map((m) => {
                  const isSelected =
                    pendingMonth &&
                    pendingMonth.year() === viewYear &&
                    pendingMonth.month() === m.index;
                  const isCurrentMonth =
                    moment().year() === viewYear && moment().month() === m.index;
                  const isDisabled = isMonthDisabled(m.index);

                  return (
                    <button
                      key={m.key}
                      type="button"
                      className={`mdtp-month-cell ${isCurrentMonth ? "is-current" : ""} ${isSelected ? "is-selected" : ""} ${isDisabled ? "is-disabled" : ""}`}
                      onClick={() => !isDisabled && handleSelectMonth(m.index)}
                      disabled={isDisabled}
                    >
                      {m.label}
                    </button>
                  );
                })}
              </div>

              {/* Action Buttons */}
              <div className="mdtp-footer">
                <button
                  type="button"
                  className="mdtp-btn-cancel"
                  onClick={handleCancel}
                >
                  {lang?.cancel || "Cancel"}
                </button>
                <button
                  type="button"
                  className="mdtp-btn-apply"
                  onClick={handleApply}
                  disabled={!pendingMonth}
                >
                  {lang?.apply || "Apply"}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
